/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *==================================================
 */

// Standard Libraries
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Popper from "@material-ui/core/Popper";
import MenuList from "@material-ui/core/MenuList";

// Custom Libraries
import { Tr, Td, Button, MenuItem, TextLink, Paper } from "../components";
import {
  TableAction,
  TableColumnDef,
  TABLE_KEY_ACTION,
  LOGONID,
  ORDERID,
} from "../constants/table";
import { useTable } from "../hooks";
import { LOGONID_V2 } from "../constants/customer";

export interface TableRowProps {
  rData: any;
  columns: TableColumnDef[];
  actions: TableAction[];
  handleViewDetails: Function;
}

const TableRow: React.FC<TableRowProps> = (props: any) => {
  const { rData, columns, actions, handleViewDetails } = props;
  const { t } = useTranslation();
  const { getCellDisplay } = useTable();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Tr>
        {columns.map((c: TableColumnDef) => {
          if (c.keyLookup.key === TABLE_KEY_ACTION) {
            return (
              <Fragment key={c.keyLookup.key}>
                {actions.length > 0 && (
                  <Td>
                    <Button
                      variant="text"
                      ref={anchorRef}
                      aria-controls={`menu-${rData[c.idProp]}`}
                      aria-haspopup="true"
                      onClick={handleToggle}>
                      {t(`${c.keyLookup.translateKeyPrefix}${c.keyLookup.key}`)}
                    </Button>
                  </Td>
                )}
              </Fragment>
            );
          } else {
            return (
              <Td key={c.keyLookup.key}>
                {c.keyLookup.key === LOGONID_V2 ||
                c.keyLookup.key === LOGONID ||
                c.keyLookup.key === ORDERID ? (
                  <TextLink
                    label={
                      t(getCellDisplay(c.keyLookup, rData)) || (
                        <span>&nbsp;</span>
                      )
                    }
                    onClick={() => {
                      handleViewDetails(rData);
                    }}
                  />
                ) : (
                  <>
                    {t(getCellDisplay(c.keyLookup, rData)) || (
                      <span>&nbsp;</span>
                    )}
                  </>
                )}
              </Td>
            );
          }
        })}
      </Tr>
      {actions.length > 0 && (
        <Popper open={open} anchorEl={anchorRef.current}>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList>
              <Paper className="table-action-menu">
                {actions.map((a: TableAction) => {
                  return (
                    <MenuItem
                      key={a.key}
                      divider
                      onClick={(event) => {
                        handleClose(event);
                        a.action();
                      }}>
                      {t(a.label())}
                    </MenuItem>
                  );
                })}
              </Paper>
            </MenuList>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default TableRow;
